import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { Box, Stack, Text, VisuallyHidden } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { Button } from 'src/design-system/Button';
import { AnimatedLogoIcon } from 'src/UI/icons';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useIsMobile } from 'src/common/hooks/useIsMobile';
import { WizardStepTransition } from '../components/WizardStepTransition';

type WelcomeScreenProps = {
  onComplete: () => void;
};

// The prototype's two-phase intro, ported onto the shared wizard step transition rather than
// its own bespoke `AnimatePresence` - see DECISIONS.md for why the "Ready to get your
// Afternoon back?" copy is kept verbatim.
export function WelcomeScreen({ onComplete }: WelcomeScreenProps) {
  const [phase, setPhase] = useState<1 | 2>(1);

  return (
    <AuthPageLayout>
      <Box pos="relative" w="100%" maw={720} px="md" py={80} mx="auto">
        <AmbientGlow />
        <WizardStepTransition stepKey={`welcome-${phase}`} direction={1} initial={true}>
          {phase === 1 ? (
            <WelcomePhaseOne onNext={() => setPhase(2)} />
          ) : (
            <WelcomePhaseTwo onNext={onComplete} />
          )}
        </WizardStepTransition>
      </Box>
    </AuthPageLayout>
  );
}

function AmbientGlow() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      aria-hidden
      initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={shouldReduceMotion ? { duration: 0 } : { duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: 'absolute',
        zIndex: -1,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 480,
        height: 480,
        borderRadius: '50%',
        background: 'radial-gradient(circle, var(--mantine-color-brand-1) 0%, transparent 70%)',
        filter: 'blur(48px)',
        pointerEvents: 'none',
      }}
    />
  );
}

function HeroHeading({ children }: { children: string }) {
  return <VisuallyHidden component="h1">{children}</VisuallyHidden>;
}

// `AuthPageLayout` renders a dark page background on desktop but falls back to a light one on
// mobile (see its own `isMobile` branch), so the hero text needs to track that same switch to
// stay readable in both cases.
function useHeroTextColor() {
  const { isMobile } = useIsMobile();
  return isMobile ? 'gray.10' : 'gray.0';
}

const cinematicFadeInUp: Variants = {
  initial: { opacity: 0, y: 30, filter: 'blur(12px)' },
  animate: (delay: number) => ({
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 1.5,
      delay: delay,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const typewriterContainer: Variants = {
  initial: { opacity: 1 },
  animate: (delay: number) => ({
    opacity: 1,
    transition: {
      delayChildren: delay,
      staggerChildren: 0.02,
    },
  }),
};

const typewriterChar: Variants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};

function AnimatedText({ text }: { text: string }) {
  const words = text.split(' ');
  return (
    <>
      {words.map((word, wordIndex) => (
        <React.Fragment key={wordIndex}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {word.split('').map((char, charIndex) => (
              <motion.span
                key={charIndex}
                variants={typewriterChar}
                style={{ display: 'inline-block' }}
              >
                {char}
              </motion.span>
            ))}
          </span>
          {wordIndex < words.length - 1 && ' '}
        </React.Fragment>
      ))}
    </>
  );
}

function WelcomePhaseOne({ onNext }: { onNext: () => void }) {
  const shouldReduceMotion = useReducedMotion();
  const initial = shouldReduceMotion ? undefined : 'initial';
  const animate = shouldReduceMotion ? undefined : 'animate';
  const textColor = useHeroTextColor();

  const [showButton, setShowButton] = useState(false);
  useEffect(() => {
    if (shouldReduceMotion) {
      setShowButton(true);
      return;
    }
    const timer = setTimeout(() => setShowButton(true), 2900);
    return () => clearTimeout(timer);
  }, [shouldReduceMotion]);

  return (
    <div>
      <HeroHeading>Ready to get your Afternoon back?</HeroHeading>
      <motion.div initial={initial} animate={animate}>
        <Stack gap="xl" align="center" ta="center">
          <motion.div>
            <Stack gap={0} align="center" aria-hidden>
              <motion.div
                custom={1.0}
                variants={typewriterContainer}
                initial={initial}
                animate={animate}
              >
                <Text fw={FONT_WEIGHTS.bold} c={textColor} style={heroLineStyle} mb={16}>
                  <AnimatedText text="Ready to get your" />
                </Text>
              </motion.div>
              <motion.div
                custom={0}
                variants={cinematicFadeInUp}
                initial={initial}
                animate={animate}
              >
                <AnimatedLogoIcon height={140} color="brand.6" />
              </motion.div>
              <motion.div
                custom={1.8}
                variants={typewriterContainer}
                initial={initial}
                animate={animate}
              >
                <Text fw={FONT_WEIGHTS.bold} c={textColor} style={heroLineStyle} mt={16}>
                  <AnimatedText text="back?" />
                </Text>
              </motion.div>
            </Stack>
          </motion.div>
          <motion.div
            initial={{ height: 0, marginTop: 0 }}
            animate={showButton ? { height: 'auto', marginTop: 64 } : { height: 0, marginTop: 0 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={showButton ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }}
              transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
              whileHover={shouldReduceMotion ? undefined : { scale: 1.05 }}
              whileTap={shouldReduceMotion ? undefined : { scale: 0.95 }}
            >
              <Button
                size="lg"
                px={64}
                style={{ borderRadius: 999, height: 64, fontSize: '1.25rem' }}
                rightSection={<IconArrowRight size={24} />}
                onClick={onNext}
              >
                Yes
              </Button>
            </motion.div>
          </motion.div>
        </Stack>
      </motion.div>
    </div>
  );
}

function WelcomePhaseTwo({ onNext }: { onNext: () => void }) {
  const shouldReduceMotion = useReducedMotion();
  const initial = shouldReduceMotion ? undefined : 'initial';
  const animate = shouldReduceMotion ? undefined : 'animate';
  const textColor = useHeroTextColor();
  const copy =
    "Let's get everything set up for you so you can start using Afternoon as quickly as possible.";

  const [showButton, setShowButton] = useState(false);
  useEffect(() => {
    if (shouldReduceMotion) {
      setShowButton(true);
      return;
    }
    const timer = setTimeout(() => setShowButton(true), 3500);
    return () => clearTimeout(timer);
  }, [shouldReduceMotion]);

  return (
    <div>
      <HeroHeading>{copy}</HeroHeading>
      <motion.div initial={initial} animate={animate}>
        <Stack gap="xl" align="center" ta="center">
          <motion.div>
            <Stack gap={0} align="center" aria-hidden>
              <motion.div
                custom={1.0}
                variants={typewriterContainer}
                initial={initial}
                animate={animate}
              >
                <Text fw={FONT_WEIGHTS.bold} c={textColor} style={heroParagraphStyle} maw={800}>
                  <AnimatedText text={copy} />
                </Text>
              </motion.div>
            </Stack>
          </motion.div>
          <motion.div
            initial={{ height: 0, marginTop: 0 }}
            animate={showButton ? { height: 'auto', marginTop: 64 } : { height: 0, marginTop: 0 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={showButton ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }}
              transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
              whileHover={shouldReduceMotion ? undefined : { scale: 1.05 }}
              whileTap={shouldReduceMotion ? undefined : { scale: 0.95 }}
            >
              <Button
                size="lg"
                px={64}
                style={{ borderRadius: 999, height: 64, fontSize: '1.25rem' }}
                rightSection={<IconArrowRight size={24} />}
                onClick={onNext}
              >
                Let&apos;s get started
              </Button>
            </motion.div>
          </motion.div>
        </Stack>
      </motion.div>
    </div>
  );
}

const heroLineStyle = {
  fontSize: 'clamp(1.8rem, 5vw, 4.0rem)',
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
};

const heroParagraphStyle = {
  fontSize: 'clamp(1.8rem, 5vw, 3.2rem)',
  lineHeight: 1.25,
  letterSpacing: '-0.01em',
  maxWidth: 800,
};
